import { Fragment, type ReactNode } from "react";
import { extractKnowledgeIds } from "../lib/knowledge";
import { parseMarkdownTable } from "../lib/markdown";

interface MarkdownProps {
  text: string;
  onKnowledgeIdClick?: (id: string) => void;
}

function inline(text: string, onKnowledgeIdClick?: (id: string) => void): ReactNode[] {
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|(?:KB|SRC|MAP|INB)-\d{6})/g;
  const parts = text.split(pattern).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`")) return <code key={index}>{part.slice(1, -1)}</code>;
    if (/^(?:KB|SRC|MAP|INB)-\d{6}$/.test(part)) {
      return onKnowledgeIdClick ? (
        <button key={index} type="button" className="knowledge-link" onClick={() => onKnowledgeIdClick(part)}>{part}</button>
      ) : <code key={index}>{part}</code>;
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

export function Markdown({ text, onKnowledgeIdClick }: MarkdownProps) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const nodes: ReactNode[] = [];
  let code: string[] | null = null;
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushList = () => {
    if (!list) return;
    const List = list.ordered ? "ol" : "ul";
    nodes.push(<List key={`list-${nodes.length}`}>{list.items.map((item, i) => <li key={i}>{inline(item, onKnowledgeIdClick)}</li>)}</List>);
    list = null;
  };
  const flushCode = () => {
    if (!code) return;
    nodes.push(<pre key={`code-${nodes.length}`}><code>{code.join("\n")}</code></pre>);
    code = null;
  };

  let lineIndex = 0;
  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? "";
    if (line.trim().startsWith("```")) {
      flushList();
      if (code) flushCode(); else code = [];
      lineIndex += 1;
      continue;
    }
    if (code) {
      code.push(line);
      lineIndex += 1;
      continue;
    }
    const ordered = line.match(/^\s*\d+[.)]\s+(.+)$/);
    const unordered = line.match(/^\s*[-*]\s+(.+)$/);
    if (ordered || unordered) {
      const isOrdered = Boolean(ordered);
      const content = (ordered ?? unordered)?.[1] ?? "";
      if (!list || list.ordered !== isOrdered) {
        flushList();
        list = { ordered: isOrdered, items: [] };
      }
      list.items.push(content);
      lineIndex += 1;
      continue;
    }
    flushList();
    if (!line.trim()) {
      lineIndex += 1;
      continue;
    }
    const table = parseMarkdownTable(lines, lineIndex);
    if (table) {
      nodes.push(
        <div className="markdown-table-wrap" key={`table-${nodes.length}`}>
          <table>
            <thead>
              <tr>
                {table.header.map((cell, index) => (
                  <th key={index} style={{ textAlign: table.alignments[index] ?? undefined }}>
                    {inline(cell, onKnowledgeIdClick)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, index) => (
                    <td key={index} style={{ textAlign: table.alignments[index] ?? undefined }}>
                      {inline(cell, onKnowledgeIdClick)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      lineIndex = table.nextIndex;
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = heading[1]?.length ?? 2;
      const content = inline(heading[2] ?? "", onKnowledgeIdClick);
      if (level === 1) nodes.push(<h1 key={nodes.length}>{content}</h1>);
      else if (level === 2) nodes.push(<h2 key={nodes.length}>{content}</h2>);
      else if (level === 3) nodes.push(<h3 key={nodes.length}>{content}</h3>);
      else nodes.push(<h4 key={nodes.length}>{content}</h4>);
      lineIndex += 1;
      continue;
    }
    nodes.push(<p key={nodes.length}>{inline(line, onKnowledgeIdClick)}</p>);
    lineIndex += 1;
  }
  flushList();
  flushCode();
  return <div className="markdown">{nodes}</div>;
}

export function citedKnowledgeIds(text: string): string[] {
  return extractKnowledgeIds(text);
}
